<!-- Editable Mode (for modal) -->
<div *ngIf="editableMode" class="editable-metadata-form">
  <div class="step-content" *ngIf="metadataForm && additionalInfoForm">
    <!-- Dataset Information Fields (Read-only: Title and Permission) -->
    <div *ngIf="datasetData" class="form-fields">
      <div class="form-row">
        <div class="form-field col-6">
          <label for="dataset-title" class="form-label">
            Title <span class="required">*</span>
          </label>
          <input
            type="text"
            id="dataset-title"
            [value]="datasetData.title"
            class="form-input"
            disabled
            readonly
          />
        </div>
        <div class="form-field col-6">
          <label for="dataset-permission" class="form-label">
            Permission <span class="required">*</span>
          </label>
          <!-- In modify mode (allowSaveWithoutDirty), use same custom dropdown style as create flow -->
          <ng-container *ngIf="allowSaveWithoutDirty; else readonlyPermission">
            <div class="custom-dropdown-wrapper">
              <div
                class="custom-dropdown-trigger"
                [class.open]="isPermissionDropdownOpen"
                (click)="togglePermissionDropdown()"
              >
                <div class="trigger-content">
                  <span
                    *ngIf="!datasetData?.permission"
                    class="placeholder"
                  >
                    Select permission
                  </span>
                  <span
                    *ngIf="datasetData?.permission"
                    class="permission-chip"
                    [class.permission-chip--public]="datasetData?.permission === 'Open'"
                    [class.permission-chip--restricted]="datasetData?.permission === 'Restricted'"
                  >
                    <img src="/public.svg" alt="" class="permission-chip__icon" aria-hidden="true" />
                    {{ getSelectedPermissionLabelForEdit() }}
                  </span>
                </div>
                <svg
                  class="dropdown-arrow"
                  [class.rotated]="isPermissionDropdownOpen"
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                >
                  <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div
                *ngIf="isPermissionDropdownOpen"
                class="custom-dropdown-panel"
                (click)="$event.stopPropagation()"
              >
                <div
                  class="dropdown-option"
                  (click)="selectPermissionOption('Open')"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      [checked]="datasetData?.permission === 'Open'"
                      class="checkbox-custom"
                      readonly
                    />
                  </div>
                  <span class="option-label">Public</span>
                </div>
                <div
                  class="dropdown-option"
                  (click)="selectPermissionOption('Restricted')"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      [checked]="datasetData?.permission === 'Restricted'"
                      class="checkbox-custom"
                      readonly
                    />
                  </div>
                  <span class="option-label">Restricted</span>
                </div>
              </div>
            </div>
          </ng-container>
          <ng-template #readonlyPermission>
            <input
              type="text"
              id="dataset-permission"
              [value]="getPermissionDisplayLabel(datasetData.permission)"
              class="form-input"
              disabled
              readonly
            />
          </ng-template>
        </div>
      </div>
      <hr class="form-divider" />
    </div>

    <!-- Metadata Form (Editable) -->
    <form [formGroup]="metadataForm">
      <!-- For Datasets -->
      <ng-container *ngIf="!isModel()">
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="short-description" class="form-label">
                Short Description <span class="required">*</span>
              </label>
              <div class="textarea-wrapper">
                <textarea
                  id="short-description"
                  formControlName="shortDescription"
                  class="form-textarea"
                  [rows]="editableMode ? 3 : 4"
                  [readonly]="!editableMode"
                  [class.error]="metadataForm.get('shortDescription')?.invalid && metadataForm.get('shortDescription')?.touched"
                ></textarea>
              </div>
              <div *ngIf="metadataForm.get('shortDescription')?.invalid && metadataForm.get('shortDescription')?.touched" class="error-message">
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['required']">Short description is required</span>
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['minlength']">Short description must be at least 50 characters</span>
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['maxlength']">Short description cannot exceed 200 characters</span>
              </div>
            </div>
            <div class="form-field col-6">
              <label for="long-description" class="form-label">
                Long Description <span class="required">*</span>
              </label>
              <div class="textarea-wrapper">
                <textarea
                  id="long-description"
                  formControlName="longDescription"
                  class="form-textarea"
                  [rows]="editableMode ? 6 : 4"
                  [readonly]="!editableMode"
                  [class.error]="metadataForm.get('longDescription')?.invalid && metadataForm.get('longDescription')?.touched"
                ></textarea>
              </div>
              <div *ngIf="metadataForm.get('longDescription')?.invalid && metadataForm.get('longDescription')?.touched" class="error-message">
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['required']">Long description is required</span>
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['minlength']">Long description must be at least 50 characters</span>
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['maxlength']">Long description cannot exceed 2000 characters</span>
              </div>
            </div>
          </div>
        </div>
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="year-range" class="form-label">
                Year Range <span class="required">*</span>
              </label>
              <app-year-range-picker
                formControlName="yearRange"
                placeholder="Choose year range"
                styleClass="form-select"
              ></app-year-range-picker>
            </div>
            <div class="form-field col-6">
              <label for="geo-coverage" class="form-label">
                Geo Coverage <span class="required">*</span>
              </label>
              <input
                type="text"
                id="geo-coverage"
                formControlName="geoCoverage"
                class="form-input"
                [readonly]="!editableMode"
                [class.error]="metadataForm.get('geoCoverage')?.invalid && metadataForm.get('geoCoverage')?.touched"
              />
              <div *ngIf="metadataForm.get('geoCoverage')?.invalid && metadataForm.get('geoCoverage')?.touched" class="error-message">
                Geo coverage is required
              </div>
            </div>
          </div>
        </div>
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="resource-server" class="form-label">
                Resource Server <span class="required">*</span>
              </label>
              <!-- In modify mode (allowSaveWithoutDirty), show read‑only input instead of dropdown -->
              <ng-container *ngIf="allowSaveWithoutDirty; else editableResourceServer">
                <input
                  type="text"
                  id="resource-server"
                  class="form-input"
                  [value]="getSelectedResourceServerLabel()"
                  disabled
                  readonly
                />
              </ng-container>
              <ng-template #editableResourceServer>
                <div class="custom-dropdown-wrapper">
                  <div
                    class="custom-dropdown-trigger"
                    [class.error]="metadataForm.get('resourceServer')?.invalid && metadataForm.get('resourceServer')?.touched"
                    [class.open]="isResourceServerDropdownOpen"
                    (click)="toggleResourceServerDropdown()"
                  >
                    <div class="trigger-content">
                      <span class="selected-value">{{ getSelectedResourceServerLabel() }}</span>
                      <svg class="dropdown-arrow" [class.open]="isResourceServerDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div class="custom-dropdown-panel" *ngIf="isResourceServerDropdownOpen">
                    <div
                      *ngFor="let option of resourceServerOptions"
                      class="dropdown-option"
                      [class.selected]="isResourceServerSelected(option.value)"
                      (click)="selectResourceServer(option)"
                    >
                      <div class="checkbox-wrapper">
                        <div class="checkbox-custom" [class.checked]="isResourceServerSelected(option.value)"></div>
                      </div>
                      <div class="option-label-wrapper">
                        <div class="option-label-name">{{ option.name }}</div>
                        <div class="option-label-url">{{ option.url }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div *ngIf="metadataForm.get('resourceServer')?.invalid && metadataForm.get('resourceServer')?.touched" class="error-message">
                  Resource server is required
                </div>
              </ng-template>
            </div>
            <div class="form-field col-6">
              <label for="upload-frequency" class="form-label">
                Upload Frequency <span class="required">*</span>
              </label>
              <div class="custom-dropdown-wrapper">
                <div
                  class="custom-dropdown-trigger"
                  [class.error]="metadataForm.get('uploadFrequency')?.invalid && metadataForm.get('uploadFrequency')?.touched"
                  [class.open]="isUploadFrequencyDropdownOpen"
                  (click)="toggleUploadFrequencyDropdown()"
                >
                  <div class="trigger-content">
                    <span class="selected-value">{{ getSelectedUploadFrequencyLabel() }}</span>
                    <svg class="dropdown-arrow" [class.open]="isUploadFrequencyDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div class="custom-dropdown-panel" *ngIf="isUploadFrequencyDropdownOpen">
                  <div
                    *ngFor="let option of uploadFrequencyOptions"
                    class="dropdown-option"
                    [class.selected]="isUploadFrequencySelected(option.value)"
                    (click)="selectUploadFrequency(option)"
                  >
                    <div class="checkbox-wrapper">
                      <div class="checkbox-custom" [class.checked]="isUploadFrequencySelected(option.value)"></div>
                    </div>
                    <span class="option-label">{{ option.label }}</span>
                  </div>
                </div>
              </div>
              <div *ngIf="metadataForm.get('uploadFrequency')?.invalid && metadataForm.get('uploadFrequency')?.touched" class="error-message">
                Upload frequency is required
              </div>
            </div>
          </div>
        </div>
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="license" class="form-label">
                License <span class="required">*</span>
              </label>
              <div class="custom-dropdown-wrapper">
                <div
                  class="custom-dropdown-trigger"
                  [class.error]="metadataForm.get('license')?.invalid && metadataForm.get('license')?.touched"
                  [class.open]="isLicenseDropdownOpen"
                  (click)="toggleLicenseDropdown()"
                >
                  <div class="trigger-content">
                    <span *ngIf="!metadataForm.get('license')?.value" class="placeholder">Select license</span>
                    <span *ngIf="metadataForm.get('license')?.value && metadataForm.get('license')?.value !== 'Other'" class="selected-value">
                      {{ getSelectedLicenseLabel() }}
                    </span>
                    <span *ngIf="metadataForm.get('license')?.value === 'Other'" class="selected-value">Other</span>
                    <svg class="dropdown-arrow" [class.open]="isLicenseDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div class="custom-dropdown-panel" *ngIf="isLicenseDropdownOpen">
                  <div
                    *ngFor="let option of licenseOptions"
                    class="dropdown-option"
                    [class.selected]="isLicenseSelected(option.value)"
                    (click)="selectLicense(option)"
                  >
                    <div class="checkbox-wrapper">
                      <div class="checkbox-custom" [class.checked]="isLicenseSelected(option.value)"></div>
                    </div>
                    <span class="option-label">{{ option.label }}</span>
                  </div>
                </div>
              </div>
              <div *ngIf="metadataForm.get('license')?.invalid && metadataForm.get('license')?.touched" class="error-message">
                License is required
              </div>
            </div>
            <div class="form-field col-6" *ngIf="isOtherLicenseSelected()">
              <label for="other-license" class="form-label">
                Specify License <span class="required">*</span>
              </label>
              <input
                type="text"
                id="other-license"
                formControlName="otherLicense"
                class="form-input"
                [readonly]="!editableMode"
              />
            </div>
          </div>
        </div>
      </ng-container>

      <!-- For Models -->
      <ng-container *ngIf="isModel()">
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="short-description" class="form-label">
                Short Description <span class="required">*</span>
              </label>
              <div class="textarea-wrapper">
                <textarea
                  id="short-description"
                  formControlName="shortDescription"
                  class="form-textarea"
                  [rows]="editableMode ? 3 : 4"
                  [readonly]="!editableMode"
                  [class.error]="metadataForm.get('shortDescription')?.invalid && metadataForm.get('shortDescription')?.touched"
                ></textarea>
              </div>
              <div *ngIf="metadataForm.get('shortDescription')?.invalid && metadataForm.get('shortDescription')?.touched" class="error-message">
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['required']">Short description is required</span>
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['minlength']">Short description must be at least 50 characters</span>
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['maxlength']">Short description cannot exceed 200 characters</span>
              </div>
            </div>
            <div class="form-field col-6">
              <label for="long-description" class="form-label">
                Long Description <span class="required">*</span>
              </label>
              <div class="textarea-wrapper">
                <textarea
                  id="long-description"
                  formControlName="longDescription"
                  class="form-textarea"
                  [rows]="editableMode ? 6 : 4"
                  [readonly]="!editableMode"
                  [class.error]="metadataForm.get('longDescription')?.invalid && metadataForm.get('longDescription')?.touched"
                ></textarea>
              </div>
              <div *ngIf="metadataForm.get('longDescription')?.invalid && metadataForm.get('longDescription')?.touched" class="error-message">
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['required']">Long description is required</span>
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['minlength']">Long description must be at least 50 characters</span>
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['maxlength']">Long description cannot exceed 2000 characters</span>
              </div>
            </div>
          </div>
        </div>
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="industry" class="form-label">
                Industry <span class="required">*</span>
              </label>
              <input
                type="text"
                id="industry"
                formControlName="industry"
                class="form-input"
                [readonly]="!editableMode"
                [class.error]="metadataForm.get('industry')?.invalid && metadataForm.get('industry')?.touched"
              />
              <div *ngIf="metadataForm.get('industry')?.invalid && metadataForm.get('industry')?.touched" class="error-message">
                Industry is required
              </div>
            </div>
            <div class="form-field col-6">
              <label for="license" class="form-label">
                License <span class="required">*</span>
              </label>
              <input
                type="text"
                id="license"
                formControlName="license"
                class="form-input"
                [readonly]="!editableMode"
                [class.error]="metadataForm.get('license')?.invalid && metadataForm.get('license')?.touched"
              />
              <div *ngIf="metadataForm.get('license')?.invalid && metadataForm.get('license')?.touched" class="error-message">
                License is required
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </form>

    <!-- Additional Info Form -->
    <form [formGroup]="additionalInfoForm">
      <div class="form-fields">
        <div class="form-row">
          <div class="form-field col-6" *ngIf="!isModel()">
            <label for="dataset-type" class="form-label">
              Dataset Type <span class="required">*</span>
            </label>
            <div class="custom-dropdown-wrapper" *ngIf="editableMode">
              <div
                class="custom-dropdown-trigger"
                [class.error]="additionalInfoForm.get('datasetType')?.invalid && additionalInfoForm.get('datasetType')?.touched"
                [class.open]="isDatasetTypeDropdownOpen"
                (click)="toggleDatasetTypeDropdown()"
              >
                <div class="trigger-content">
                  <span class="selected-value">{{ getSelectedDatasetTypeLabel() }}</span>
                  <svg class="dropdown-arrow" [class.open]="isDatasetTypeDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="custom-dropdown-panel" *ngIf="isDatasetTypeDropdownOpen">
                <div
                  *ngFor="let option of datasetTypeOptions"
                  class="dropdown-option"
                  [class.selected]="isDatasetTypeSelected(option.value)"
                  (click)="selectDatasetType(option)"
                >
                  <div class="checkbox-wrapper">
                    <div class="checkbox-custom" [class.checked]="isDatasetTypeSelected(option.value)"></div>
                  </div>
                  <span class="option-label">{{ option.label }}</span>
                </div>
              </div>
            </div>
            <input
              *ngIf="!editableMode"
              type="text"
              id="dataset-type"
              formControlName="datasetType"
              class="form-input"
              readonly
            />
            <div *ngIf="additionalInfoForm.get('datasetType')?.invalid && additionalInfoForm.get('datasetType')?.touched" class="error-message">
              Dataset type is required
            </div>
          </div>
          <div class="form-field col-6">
            <label for="file-format" class="form-label">
              File Format <span class="required">*</span>
            </label>
            <div class="custom-dropdown-wrapper" *ngIf="editableMode">
              <div
                class="custom-dropdown-trigger"
                [class.error]="additionalInfoForm.get('fileFormat')?.invalid && additionalInfoForm.get('fileFormat')?.touched"
                [class.open]="isFileFormatDropdownOpen"
                (click)="toggleFileFormatDropdown()"
              >
                <div class="trigger-content">
                  <span class="selected-value">{{ getSelectedFileFormatLabel() }}</span>
                  <svg class="dropdown-arrow" [class.open]="isFileFormatDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="custom-dropdown-panel" *ngIf="isFileFormatDropdownOpen">
                <div
                  *ngFor="let option of fileFormatOptions"
                  class="dropdown-option"
                  [class.selected]="isFileFormatSelected(option.value)"
                  (click)="selectFileFormat(option)"
                >
                  <div class="checkbox-wrapper">
                    <div class="checkbox-custom" [class.checked]="isFileFormatSelected(option.value)"></div>
                  </div>
                  <span class="option-label">{{ option.label }}</span>
                </div>
              </div>
            </div>
            <input
              *ngIf="!editableMode"
              type="text"
              id="file-format"
              formControlName="fileFormat"
              class="form-input"
              readonly
            />
            <div *ngIf="additionalInfoForm.get('fileFormat')?.invalid && additionalInfoForm.get('fileFormat')?.touched" class="error-message">
              File format is required
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-field" [class.col-6]="hasDataSource()" [class.col-12]="!hasDataSource()">
            <label for="tags" class="form-label">
              Tags <span class="required">*</span>
            </label>
            <div class="tags-input-wrapper" *ngIf="editableMode">
              <div class="tags-input-container">
                <input
                  type="text"
                  id="tags"
                  class="tags-input"
                  placeholder="Enter a tag"
                  [(ngModel)]="tagInput"
                  [ngModelOptions]="{standalone: true}"
                  (keydown)="onTagInputKeydown($event)"
                />
                <button
                  type="button"
                  class="tags-add-button"
                  (click)="addTag()"
                  [disabled]="!tagInput || tagInput.trim().length === 0"
                  title="Add tag"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              <div class="tags-container" *ngIf="getTags().length > 0">
                <span *ngFor="let tag of getTags()" class="tag-chip" [class.tag-chip--duplicate]="isTagDuplicate(tag)">
                  {{ tag }}
                  <svg
                    class="tag-chip-close"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    (click)="removeTag(tag)"
                  >
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </div>
              <div *ngIf="getTags().length === 0" class="no-tags-message">
                No tags added
              </div>
              <div *ngIf="additionalInfoForm.get('tags')?.invalid && additionalInfoForm.get('tags')?.touched" class="error-message">
                At least one tag is required
              </div>
            </div>
            <div *ngIf="!editableMode">
              <div class="tags-container" *ngIf="getTags().length > 0">
                <span *ngFor="let tag of getTags()" class="tag-chip">
                  {{ tag }}
                </span>
              </div>
              <div *ngIf="getTags().length === 0" class="no-tags-message">
                No tags added
              </div>
            </div>
          </div>
          <div class="form-field col-6" *ngIf="hasDataSource()">
            <label class="form-label">
              Data Source
              <span class="required" *ngIf="isModel()">*</span>
            </label>
            <input
              type="url"
              formControlName="sampleDataUrl"
              class="form-input"
              [readonly]="!editableMode"
            />
          </div>
        </div>
      </div>
    </form>

    <!-- Submit/Cancel Buttons -->
    <div class="form-actions" *ngIf="editableMode">
      <button type="button" class="btn btn-secondary" (click)="onCancel()">Cancel</button>
      <button 
        type="button" 
        class="btn btn-primary" 
        (click)="onSaveChangesClick()" 
        [disabled]="isSaveButtonDisabled"
      >
        Save Changes
      </button>
    </div>
  </div>
</div>

<!-- Original Preview Mode (for stepper flow) -->
<ng-container *ngIf="!editableMode">
<app-header [forceLightBackground]="true"></app-header>
<div class="provider-dataset">
  <div class="provider-dataset--layout">
    <app-org-admin-sidebar *ngIf="isOrgAdminRoute" class="provider-dataset--sidebar"></app-org-admin-sidebar>
    <app-provider-sidebar *ngIf="!isOrgAdminRoute" class="provider-dataset--sidebar"></app-provider-sidebar>
    <div class="provider-dataset--content">
      <app-page-header></app-page-header>
      <div class="dataset-container">
        <!-- Step 3 Template -->
        <ng-template #step3Template>
          <div class="step-content">
            <h2 class="step-title">Preview Metadata</h2>
            
            <!-- Dataset Information Fields (Disabled) -->
            <div *ngIf="datasetData" class="form-fields">
              <div class="form-row">
                <!-- Title Field (Disabled) -->
                <div class="form-field col-6">
                  <label for="dataset-title" class="form-label">
                    Title <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dataset-title"
                    [value]="datasetData.title"
                    class="form-input"
                    disabled
                  />
                </div>

                <!-- Permission Field (Disabled) -->
                <div class="form-field col-6">
                  <label for="dataset-permission" class="form-label">
                    Permission <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dataset-permission"
                    [value]="getPermissionDisplayLabel(datasetData.permission)"
                    class="form-input"
                    disabled
                  />
                </div>
              </div>
              
              <!-- HR Line -->
              <hr class="form-divider" />
            </div>

            <!-- Step 2: Metadata Information (Read-only) -->
            <form [formGroup]="metadataForm">
              <!-- For Datasets: All Metadata Fields -->
              <ng-container *ngIf="!isModel()">
                <!-- Description Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Short Description -->
                    <div class="form-field col-6">
                      <label for="short-description" class="form-label">
                        Short Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="short-description"
                          formControlName="shortDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>

                    <!-- Long Description -->
                    <div class="form-field col-6">
                      <label for="long-description" class="form-label">
                        Long Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="long-description"
                          formControlName="longDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Year Range and Geo Coverage Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Year Range Field -->
                    <div class="form-field col-6">
                      <label for="year-range" class="form-label">
                        Year Range <span class="required">*</span>
                      </label>
                      <app-year-range-picker
                        formControlName="yearRange"
                        placeholder="Choose year range"
                        styleClass="form-select"
                      ></app-year-range-picker>
                    </div>

                    <!-- Geo Coverage Field -->
                    <div class="form-field col-6">
                      <label for="geo-coverage" class="form-label">
                        Geo Coverage <span class="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="geo-coverage"
                        formControlName="geoCoverage"
                        class="form-input"
                        readonly
                      />
                    </div>
                  </div>
                </div>

                <!-- Resource Server and Upload Frequency Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Resource Server Field -->
                    <div class="form-field col-6">
                      <label for="resource-server" class="form-label">
                        Resource Server <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getResourceServerDisplay(metadataForm.get('resourceServer')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Upload Frequency Field -->
                    <div class="form-field col-6">
                      <label for="upload-frequency" class="form-label">
                        Upload Frequency <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getUploadFrequencyLabel(metadataForm.get('uploadFrequency')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- License Field -->
                <div class="form-fields">
                  <div class="form-row">
                    <div class="form-field col-6">
                      <label for="license" class="form-label">
                        License <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getLicenseLabel(metadataForm.get('license')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Other License Input (shown when "Other" is selected) -->
                    <div 
                      class="form-field col-6" 
                      *ngIf="isOtherLicenseSelected()"
                    >
                      <label for="other-license" class="form-label">
                        Specify License <span class="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="other-license"
                        formControlName="otherLicense"
                        class="form-input"
                        readonly
                      />
                    </div>
                  </div>
                </div>
              </ng-container>

              <!-- For Models: Description Fields, Industry and License -->
              <ng-container *ngIf="isModel()">
                <!-- Description Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Short Description -->
                    <div class="form-field col-6">
                      <label for="short-description" class="form-label">
                        Short Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="short-description"
                          formControlName="shortDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>

                    <!-- Long Description -->
                    <div class="form-field col-6">
                      <label for="long-description" class="form-label">
                        Long Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="long-description"
                          formControlName="longDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Industry and License Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Industry Field -->
                    <div class="form-field col-6">
                      <label for="industry" class="form-label">
                        Industry <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getIndustryLabel(metadataForm.get('industry')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- License Field -->
                    <div class="form-field col-6">
                      <label for="license" class="form-label">
                        License <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getLicenseLabel(metadataForm.get('license')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </form>

            <!-- Step 3: Additional Information (Read-only) -->
            <form [formGroup]="additionalInfoForm">
              <div class="form-fields">
                <div class="form-row">
                  <!-- Dataset / Model Type Field -->
                  <div class="form-field col-6">
                    <label for="dataset-type" class="form-label">
                      {{ isModel() ? 'Model Type' : 'Dataset Type' }}
                      <span class="required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div class="custom-dropdown-trigger disabled">
                        <div class="trigger-content">
                          <span class="selected-value">
                            {{ getDatasetTypeLabel(additionalInfoForm.get('datasetType')?.value) || 'Not selected' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- File Format Field -->
                  <div class="form-field col-6">
                    <label for="file-format" class="form-label">
                      File Format <span class="required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div class="custom-dropdown-trigger disabled">
                        <div class="trigger-content">
                          <span class="selected-value">
                            {{ getFileFormatLabel(additionalInfoForm.get('fileFormat')?.value) || 'Not selected' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tags and Data Source Row -->
                <div class="form-row">
                  <!-- Tags Field -->
                  <div class="form-field" [class.col-6]="hasDataSource()" [class.col-12]="!hasDataSource()">
                    <label for="tags" class="form-label">
                      Tags <span class="required">*</span>
                    </label>
                    <div class="tags-container" *ngIf="getTags().length > 0">
                      <span
                        *ngFor="let tag of getTags()"
                        class="tag-chip"
                      >
                        {{ tag }}
                      </span>
                    </div>
                    <div *ngIf="getTags().length === 0" class="no-tags-message">
                      No tags added
                    </div>
                  </div>

                  <!-- Data Source Field - Only show if not empty or required for models -->
                  <div class="form-field col-6" *ngIf="hasDataSource()">
                    <label class="form-label">
                      Data Source
                      <span class="required" *ngIf="isModel()">*</span>
                    </label>
                    <input
                      type="url"
                      formControlName="sampleDataUrl"
                      class="form-input"
                      readonly
                    />
                  </div>
                </div>

                <!-- Data Descriptor Field (only for datasets) - Only show if there are descriptors -->
                @if (!isModel() && dataDescriptorsList.length > 0) {
                  <div class="form-field">
                    <label class="form-label">Data Descriptor</label>
                    <div class="descriptor-table-wrapper">
                      <table class="descriptor-table">
                        <thead>
                          <tr>
                            <th>Attribute Name</th>
                            <th>Parameter Type</th>
                            <th>Schema Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (descriptor of dataDescriptorsList; track $index) {
                            <tr>
                              <td>{{ descriptor.attributeName }}</td>
                              <td>{{ descriptor.parameterType }}</td>
                              <td>
                                <span class="schema-badge">{{ descriptor.schemaType }}</span>
                              </td>
                            </tr>
                          } @empty {
                            <tr>
                              <td colspan="3" class="descriptor-empty-row">No data descriptors available</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                }
              </div>
            </form>
          </div>
        </ng-template>

        <!-- Stepper Component -->
        <app-stepper
          [steps]="stepperStepsWithLabel"
          [currentStep]="currentStep"
          (stepChange)="onStepChange($event)"
          (next)="onNext()"
          (previous)="onPrevious()"
        >        </app-stepper>
      </div>
    </div>
  </div>
</div>
</ng-container>